/**
 * @file: Inventory.jsx
 * @status: Intellectual Capital Management (V12.6.1) - COMPACT ACTIVE & ACCESSIBILITY ENHANCED
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.28
 * @description: تحديث المقاسات للرؤية الكاملة 100%؛ إضافة سمات id/name لرادار البحث؛ استخدام شعار logo2026.png المعتمد.
 */

import React, { useState, useEffect, useCallback } from 'react';
import hugerLogo from '../assets/logo2026.png'; // تم التحديث ليتوافق مع رادار الجهاز
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Lightbulb, Box, Search, Filter, 
  Shield, CheckCircle, Microscope, Rocket,
  Cpu, Zap, Database, Award, ArrowUpRight,
  Globe, Briefcase, FileText, Loader2
} from 'lucide-react';
import { Tag, Tooltip, Badge, message } from 'antd';

const Inventory = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [patents, setPatents] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- [ إعدادات الجسر السحابي السيادي ] ---
  const API_URL = "https://portal.huger.sa/api.php";

  // --- [ دالة جلب براءات الاختراع - Stealth Mode ] ---
  const fetchPatents = useCallback(async () => {
    setLoading(true);
    try {
      // الحفاظ على بروتوكول POST لضمان أمن البيانات السيادي
      const response = await axios.post(API_URL, { 
        action: 'p_radar' 
      });
      
      if (Array.isArray(response.data)) {
        setPatents(response.data);
      }
    } catch (error) {
      console.error("عائق في سحب مخزون الابتكار");
      message.error(i18n.language === 'ar' ? "عذراً رئيس، تعذر الوصول لمخزون الابتكار." : "CEO, Intellectual Depot access failed.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, i18n.language]);

  useEffect(() => {
    fetchPatents();
  }, [fetchPatents]);

  const getIcon = (field) => {
    switch (field?.toUpperCase()) {
      case 'STEAM': return <Zap className="text-blue-400" size={24} />;
      case 'SPACE': return <Rocket className="text-purple-400" size={24} />;
      case 'CHEMISTRY': return <Microscope className="text-emerald-400" size={24} />;
      case 'ROBOTICS': return <Cpu className="text-red-400" size={24} />;
      default: return <Lightbulb className="text-[#F57C00]" size={24} />;
    }
  };

  return (
    <div className={`min-h-screen bg-[#050B14] p-4 md:p-8 font-sans text-white relative overflow-x-hidden ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      {/* 🛡️ الإضاءة الجمالية الخلفية */}
      <div className="absolute top-0 right-0 w-[40%] h-[40%] bg-[#F57C00]/5 rounded-full blur-[100px] pointer-events-none"></div>

      <header className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center mb-8 gap-6 border-b border-white/5 pb-6 relative z-10 scale-100 transition-transform">
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="bg-gradient-to-br from-[#F57C00] to-orange-600 p-3 rounded-2xl shadow-lg relative z-10">
              <img src={hugerLogo} alt="Logo" className="w-8 h-8 object-contain" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black italic text-white tracking-tighter leading-none uppercase">
              {i18n.language === 'ar' ? 'مخزون' : 'INNOVATION'} <span className="text-[#F57C00]">{i18n.language === 'ar' ? 'الابتكار' : 'DEPOT'}</span>
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-slate-500 font-black text-[8px] uppercase tracking-[0.3em]">Intellectual Capital • HUGER 2026</span>
              <Badge count={patents.length > 0 ? `${patents.length}` : "3000"} overflowCount={9999} style={{ backgroundColor: '#F57C00', color: 'white', fontWeight: '900', fontSize: '8px', border: 'none' }} />
            </div>
          </div>
        </div>
        
        <div className="flex gap-4 w-full md:w-1/3">
          <div className="relative w-full">
            <Search className={`absolute ${i18n.language === 'ar' ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-slate-500`} size={16} />
            <input 
              id="innovation_search_depot"
              name="innovation_query"
              type="text" 
              placeholder={i18n.language === 'ar' ? "رادار البحث بختم @CEO..." : "Search Radar @CEO..."} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="on"
              className={`bg-white/5 border border-white/10 rounded-xl py-4 text-sm text-white w-full outline-none focus:border-[#F57C00] focus:bg-white/10 transition-all placeholder:text-slate-600 ${i18n.language === 'ar' ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
            />
          </div>
        </div>
      </header>

      {/* لوحة التحكم الإحصائية */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 relative z-10">
          {[
            { icon: <FileText className="text-blue-500" size={16}/>, label: i18n.language === 'ar' ? 'أصول فكرية' : 'Assets', val: patents.length || '3000+', bg: 'blue' },
            { icon: <CheckCircle className="text-emerald-500" size={16}/>, label: i18n.language === 'ar' ? 'جاهز للتصنيع' : 'Production Ready', val: '40%', bg: 'emerald' },
            { icon: <Award className="text-[#F57C00]" size={16}/>, label: 'Sovereign Seal', val: '100%', bg: 'orange' },
            { icon: <Briefcase className="text-purple-500" size={16}/>, label: i18n.language === 'ar' ? 'توطين صناعي' : 'Localization', val: '88%', bg: 'purple' }
          ].map((stat, i) => (
          <div key={i} className="bg-[#0A192F]/60 border border-white/5 p-5 rounded-3xl flex items-center gap-4 hover:border-[#F57C00]/20 transition-all">
            <div className={`p-3 bg-white/5 rounded-2xl`}>{stat.icon}</div>
            <div>
              <p className="text-[7px] font-black text-slate-500 uppercase tracking-widest leading-none mb-2">{stat.label}</p>
              <h4 className="text-xl font-black text-white leading-none">{stat.val}</h4>
            </div>
          </div>
          ))}
      </div>

      <main className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
        {loading ? (
          <div className="col-span-full py-24 text-center flex flex-col items-center gap-6">
            <Loader2 className="animate-spin text-[#F57C00]" size={40} />
            <p className="text-slate-500 font-black uppercase tracking-widest text-xs italic">
              {i18n.language === 'ar' ? 'جاري استحضار مخزون الابتكار بختم @CEO...' : 'Invoking Innovation Depot @CEO...'}
            </p>
          </div>
        ) : patents.filter(p => (p.title_ar || "").toLowerCase().includes(searchTerm.toLowerCase())).map(item => (
          <div key={item.id} className="bg-gradient-to-br from-[#0A192F]/80 to-transparent border border-white/5 rounded-[3rem] p-8 hover:border-[#F57C00]/50 transition-all duration-500 group relative overflow-hidden shadow-2xl">
            <div className="flex justify-between items-start mb-8">
              <div className="w-16 h-16 bg-black/50 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform border border-white/10 shadow-inner">
                {getIcon(item.field)}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="bg-[#F57C00]/10 p-2 rounded-xl text-[#F57C00] opacity-0 group-hover:opacity-100 transition-all">
                  <ArrowUpRight size={20} />
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-black text-white mb-4 italic tracking-tighter leading-tight h-16 overflow-hidden">
              {item.title_ar}
            </h2>
            
            <div className="flex flex-wrap gap-2 mb-8">
                <Tag className="bg-blue-900/30 text-blue-400 border-none font-black rounded-lg text-[9px] px-3 py-1 uppercase tracking-widest">
                  {item.field || "STEAM"}
                </Tag>
                <Tag className="bg-orange-900/20 text-orange-400 border-none font-black rounded-lg text-[9px] px-3 py-1 uppercase tracking-widest">
                  ID: {item.patent_id}
                </Tag>
            </div>

            <div className="flex justify-between items-center pt-6 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Shield size={12} className="text-[#F57C00]"/>
                <span className="text-[9px] text-slate-500 font-black uppercase italic tracking-widest">HUGER Certified</span>
              </div>
              <span className={`text-[9px] font-black px-4 py-1.5 rounded-full border shadow-lg ${
                item.status === 'Registered' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' : 
                'bg-blue-500/10 text-blue-400 border-blue-500/30'
              }`}>
                {item.status?.toUpperCase() || "ACTIVE"}
              </span>
            </div>
          </div>
        ))}
      </main>

      <footer className="max-w-7xl mx-auto mt-20 text-center border-t border-white/5 pt-12 pb-10">
        <div className="flex flex-col items-center gap-4 opacity-30">
          <img src={hugerLogo} alt="Seal" className="w-12 h-12 grayscale brightness-200" />
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.6em] italic">
            Dakhama & HUGER Intellectual Property Fortress • TAIF • 2026
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Inventory;